const { vestResolver } = require('@hookform/resolvers/vest')
import {
  Button,
  Grid,
  IconPlus,
  IconTrash,
  Text,
  useOnKeyDown,
} from '@vtex/admin-ui'
import { TextInput, useFormState, useWatch } from '@vtex/admin-ui-form'
import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { LIMITS } from 'shared'
import type { AdditionalInformation } from 'typings'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  type UseDrawerStateReturn,
} from '../../../Drawer'
import styles from './editor.css'
import { messages } from './messages'
import { buildValidationSuite } from './validation'

type Props = {
  state: UseDrawerStateReturn
  initialValues: AdditionalInformation[]
  onSave: (values: AdditionalInformation[]) => void
}

export const BASE_PATH = 'entries' as const

const EMPTY_ENTRY = Object.freeze({ name: '', value: '' })

const EMPTY_FORM = Object.freeze([EMPTY_ENTRY])

export function fieldName(index: number, key: keyof AdditionalInformation) {
  return `${BASE_PATH}.${index}.${key}`
}

export function Editor(props: Props) {
  const { state, initialValues, onSave } = props
  const intl = useIntl()
  const { formatMessage: i18n } = intl

  const validationSuite = useMemo(() => buildValidationSuite(intl), [intl])

  const form = useFormState({
    defaultValues: { [BASE_PATH]: [] as AdditionalInformation[] },
    resolver: vestResolver(validationSuite),
  })

  const entries: AdditionalInformation[] = useWatch({
    form,
    name: BASE_PATH,
  })

  const isCreating = initialValues.length === 0

  const setInitialValues = useCallback(() => {
    const values = structuredClone(isCreating ? EMPTY_FORM : initialValues)

    form.resetField(BASE_PATH, { defaultValue: values })
  }, [form, initialValues, isCreating])

  useEffect(() => {
    state.open && setInitialValues()
  }, [setInitialValues, state.open])

  const addEmptyEntry = useCallback(() => {
    form.setValue(BASE_PATH, [...entries, { ...EMPTY_ENTRY }])
  }, [entries, form])

  const removeEntry = (index: number) => {
    form.unregister(`${BASE_PATH}.${index}`)

    const newEntries = [...entries]
    newEntries.splice(index, 1)

    form.setValue(BASE_PATH, newEntries, { shouldValidate: true })
  }

  const handleSave = async () => {
    const valid = await form.trigger()

    if (valid) {
      const validEntries = entries.filter((e) => e.name && e.value)
      onSave(validEntries)

      state.hide()
    }
  }

  const nameKeyDownHandler = useOnKeyDown({
    keyMap: () => ({
      Enter: (event) => {
        const entryName = (event.target as HTMLInputElement).name
        const entryValue = entryName.replace('name', 'value')
        document.getElementsByName(entryValue)[0]?.focus()
      },
    }),
  })

  const valueKeyDownHandler = useOnKeyDown({
    keyMap: () => ({
      Enter: async (event) => {
        const entryName = (event.target as HTMLInputElement).name
        const index = parseInt(entryName.split('.')[1])
        const canAddNewEntry =
          entries.length < LIMITS.MAX_ADDITIONAL_INFORMATIONS &&
          index === entries.length - 1

        if (canAddNewEntry) {
          await addEmptyEntry()
        }

        const nextEntryName = fieldName(index + 1, 'name')
        document.getElementsByName(nextEntryName)[0]?.focus()
      },
    }),
  })

  useEffect(() => {
    const index = entries.length - 1
    document.getElementsByName(fieldName(index, 'name'))[0]?.focus()
  }, [entries.length])

  const hideRemoveButton = entries.length === 1

  const hideAddButton = entries.length === LIMITS.MAX_ADDITIONAL_INFORMATIONS

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>{i18n(messages.title)}</DrawerTitle>
        <DrawerSubtitle>{i18n(messages.subtitle)}</DrawerSubtitle>
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <Grid
          templateColumns={hideRemoveButton ? '1fr 1fr' : '1fr 1fr auto'}
          className={styles.content}
        >
          {entries.map((_, index) => {
            return (
              <>
                <TextInput
                  name={fieldName(index, 'name')}
                  state={form}
                  label={i18n(messages.name)}
                  onKeyDown={nameKeyDownHandler}
                  data-testid={fieldName(index, 'name')}
                />
                <TextInput
                  name={fieldName(index, 'value')}
                  state={form}
                  label={i18n(messages.value)}
                  onKeyDown={valueKeyDownHandler}
                  data-testid={fieldName(index, 'value')}
                />
                <Button
                  hidden={hideRemoveButton}
                  className={styles.removeButton}
                  icon={<IconTrash />}
                  onClick={() => removeEntry(index)}
                  aria-label={i18n(messages.remove)}
                  variant="neutralTertiary"
                  size="large"
                  data-testid={`${BASE_PATH}.${index}.remove`}
                />
              </>
            )
          })}
          <div>
            <Button
              hidden={hideAddButton}
              variant="neutralTertiary"
              onClick={addEmptyEntry}
              icon={<IconPlus />}
              aria-label={i18n(messages.add)}
            />
          </div>
          <Text className={styles.counter} variant="body" tone="secondary">
            {i18n(messages.count, {
              count: entries.length,
              max: LIMITS.MAX_ADDITIONAL_INFORMATIONS,
            })}
          </Text>
        </Grid>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={state.hide} />
        <SaveButton isCreating={isCreating} onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
